/**
 * The chart time window, as a value the whole screen shares.
 *
 * Two kinds, and the distinction is load-bearing for auto-refresh: a RELATIVE
 * range ("last 6h") re-resolves to a new absolute window every tick, so the
 * charts roll forward; an ABSOLUTE one (a drag-zoom, a pan, a custom pick) is a
 * fixed pair of instants and refreshing it re-reads the same window. Grafana
 * draws the same line, for the same reason.
 *
 * Everything here is pure and in epoch SECONDS -- Prometheus's unit, uPlot's
 * unit, and small enough to live in a URL -- so it is unit-tested without a DOM.
 */
export interface QuickRange {
  key: string;
  seconds: number;
}

export interface RelativeRange {
  kind: 'relative';
  key: string;
  seconds: number;
}
export interface AbsoluteRange {
  kind: 'absolute';
  from: number;
  to: number;
}
export type TimeRange = RelativeRange | AbsoluteRange;

export interface Window {
  from: number;
  to: number;
}

export const DEFAULT_RANGE_KEY = '6h';
/** Below this a window is a handful of scrape samples, not a chart. */
export const MIN_SPAN_S = 60;

export const relative = (quick: QuickRange): RelativeRange => ({
  kind: 'relative',
  key: quick.key,
  seconds: quick.seconds,
});

export const absolute = (from: number, to: number): AbsoluteRange => ({
  kind: 'absolute',
  from: Math.round(from),
  to: Math.round(to),
});

export const resolve = (range: TimeRange, nowSec: number): Window =>
  range.kind === 'relative' ? { from: nowSec - range.seconds, to: nowSec } : { from: range.from, to: range.to };

export const spanOf = (range: TimeRange): number =>
  range.kind === 'relative' ? range.seconds : range.to - range.from;

export const sameRange = (a: TimeRange, b: TimeRange): boolean =>
  a.kind === 'relative'
    ? b.kind === 'relative' && a.key === b.key
    : b.kind === 'absolute' && a.from === b.from && a.to === b.to;

/**
 * The URL is the state. `?range=6h` or `?from=<s>&to=<s>`, alongside whatever
 * else is in the query string (the fixture preview keeps its `tab` there), so a
 * zoomed window pasted into a chat opens on the same window.
 */
export const fromParams = (params: URLSearchParams, quick: QuickRange[], fallback = DEFAULT_RANGE_KEY): TimeRange => {
  if (params.has('from') && params.has('to')) {
    const from = Number(params.get('from'));
    const to = Number(params.get('to'));
    if (Number.isInteger(from) && Number.isInteger(to) && to - from >= MIN_SPAN_S) return absolute(from, to);
  }
  const key = params.get('range') ?? fallback;
  const found = quick.find((entry) => entry.key === key) ?? quick.find((entry) => entry.key === fallback) ?? quick[0];
  return relative(found);
};

export const writeParams = (params: URLSearchParams, range: TimeRange): URLSearchParams => {
  const next = new URLSearchParams(params);
  next.delete('range');
  next.delete('from');
  next.delete('to');
  if (range.kind === 'relative') next.set('range', range.key);
  else {
    next.set('from', String(range.from));
    next.set('to', String(range.to));
  }
  return next;
};

/** What the API is asked for. Always absolute: see useChartBoard.ts for why. */
export const queryFor = (window: Window): string => `from=${window.from}&to=${window.to}`;

/** A drag on the plot. Null when the selection is too narrow to mean anything. */
export const zoomTo = (from: number, to: number): AbsoluteRange | null => {
  const a = Math.round(Math.min(from, to));
  const b = Math.round(Math.max(from, to));
  return b - a >= MIN_SPAN_S ? absolute(a, b) : null;
};

/** Twice the span around the same centre, never past now, never past the cap. */
export const zoomOut = (range: TimeRange, nowSec: number, maxSpanSec: number): AbsoluteRange => {
  const { from, to } = resolve(range, nowSec);
  const span = Math.min(maxSpanSec, (to - from) * 2);
  const centre = (from + to) / 2;
  const nextTo = Math.min(nowSec, centre + span / 2);
  return absolute(nextTo - span, nextTo);
};

/** Half a window left or right. Panning past now clamps to now rather than into the future. */
export const shift = (range: TimeRange, direction: -1 | 1, nowSec: number): AbsoluteRange => {
  const { from, to } = resolve(range, nowSec);
  const span = to - from;
  const delta = Math.round(span / 2) * direction;
  let nextFrom = from + delta;
  let nextTo = to + delta;
  if (nextTo > nowSec) {
    nextTo = nowSec;
    nextFrom = nowSec - span;
  }
  return absolute(nextFrom, nextTo);
};

export const humanSpan = (seconds: number): string => {
  if (seconds % 86_400 === 0) return `${seconds / 86_400} day${seconds === 86_400 ? '' : 's'}`;
  if (seconds % 3600 === 0) return `${seconds / 3600} hour${seconds === 3600 ? '' : 's'}`;
  if (seconds % 60 === 0) return `${seconds / 60} minute${seconds === 60 ? '' : 's'}`;
  return `${seconds} seconds`;
};

export const humanStep = (seconds: number | null | undefined): string => {
  if (!seconds) return '';
  if (seconds >= 3600 && seconds % 3600 === 0) return `${seconds / 3600}h`;
  if (seconds >= 60 && seconds % 60 === 0) return `${seconds / 60}m`;
  return `${seconds}s`;
};

const stamp = (seconds: number, withDate: boolean, locale?: string): string =>
  new Date(seconds * 1000).toLocaleString(locale, {
    ...(withDate ? { month: 'short', day: 'numeric' } : {}),
    hour: '2-digit',
    minute: '2-digit',
  });

/** The toolbar's caption: "Last 6 hours", or the two instants of a fixed window. */
export const describe = (range: TimeRange, locale?: string): string => {
  if (range.kind === 'relative') return `Last ${humanSpan(range.seconds)}`;
  const sameDay = new Date(range.from * 1000).toDateString() === new Date(range.to * 1000).toDateString();
  return `${stamp(range.from, true, locale)} – ${stamp(range.to, !sameDay, locale)}`;
};

/**
 * Grafana's Explore, on the default datasource, with the panel's PromQL and the
 * same window. The `left=` form is the long-lived one; Grafana rewrites it into
 * its current pane format on arrival.
 */
export const grafanaExploreUrl = (grafanaUrl: string, expr: string, range: TimeRange): string => {
  const window =
    range.kind === 'relative'
      ? { from: `now-${range.key}`, to: 'now' }
      : { from: String(range.from * 1000), to: String(range.to * 1000) };
  const left = JSON.stringify({ queries: [{ refId: 'A', expr }], range: window });
  return `${grafanaUrl.replace(/\/$/, '')}/explore?left=${encodeURIComponent(left)}`;
};

/** `datetime-local` speaks local time without a zone; these translate both ways. */
export const toLocalInput = (seconds: number): string => {
  const date = new Date(seconds * 1000);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export const fromLocalInput = (value: string): number | null => {
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
};

/** The auto-refresh cadences the toolbar offers. `off` is first and is the default. */
export const REFRESH_OPTIONS: { key: string; ms: number; label: string }[] = [
  { key: 'off', ms: 0, label: 'Off' },
  { key: '30s', ms: 30_000, label: 'Every 30s' },
  { key: '1m', ms: 60_000, label: 'Every 1m' },
  { key: '5m', ms: 300_000, label: 'Every 5m' },
];
